import { useThree } from "@react-three/fiber";
import { useLayoutEffect, useState } from "react";
import type { ColorRepresentation, Vector3 } from "three";
// From `three` itself, not `three-stdlib`: these are runtime values, and
// `three-stdlib` is only a devDependency of this package while `three` is a
// peerDependency the consumer must provide anyway. `three-stdlib`'s lines are a
// port of exactly these classes, and the repo already imports other
// `three/examples/jsm` modules.
import { Line2 } from "three/examples/jsm/lines/Line2.js";
import { LineGeometry } from "three/examples/jsm/lines/LineGeometry.js";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial.js";
import { LineSegments2 } from "three/examples/jsm/lines/LineSegments2.js";
import { LineSegmentsGeometry } from "three/examples/jsm/lines/LineSegmentsGeometry.js";

export type StableLinePoint = Vector3 | [number, number, number];

export interface StableLineProps {
  points: StableLinePoint[];
  segments?: boolean;
  color: ColorRepresentation;
  opacity: number;
  lineWidth: number;
  renderOrder: number;
  dashed?: boolean;
  dashSize?: number;
  gapSize?: number;
  userData?: Record<string, unknown>;
}

const flatten = (points: StableLinePoint[]): number[] => {
  const out: number[] = [];
  for (const point of points) {
    if (Array.isArray(point)) out.push(point[0], point[1], point[2]);
    else out.push(point.x, point.y, point.z);
  }
  return out;
};

/**
 * A `Line2` whose geometry and material outlive a change of its points.
 *
 * Written because drei's `<Line>` cannot be used for a stroke that moves.
 * There, the geometry is a `useMemo` on `points` and the material is a
 * `useState` created once, but the disposal effect is keyed on the geometry
 * and disposes *both*:
 *
 *     const [lineMaterial] = useState(() => new LineMaterial());
 *     const lineGeom = useMemo(() => {…}, [points, …]);
 *     useEffect(() => () => { lineGeom.dispose(); lineMaterial.dispose(); }, [lineGeom]);
 *
 * So every change of `points` disposes a material that is still in use. three
 * refcounts programs and deletes one when its last material goes, which makes
 * the next frame link it again — a synchronous driver stall. The drag guides
 * move with the pointer, so this fired throughout a gesture: measured at 5
 * `linkProgram` and 7 `deleteProgram` calls during a single 20-move drag, with
 * four of five vertex compiles carrying `USE_LINE_COLOR_ALPHA`.
 *
 * Here the line and the material are built once and only the geometry's
 * buffers are rebuilt, so the material — and with it the program — survives
 * every change of the points.
 */
export const StableLine = ({
  points,
  segments = false,
  color,
  opacity,
  lineWidth,
  renderOrder,
  dashed = false,
  dashSize,
  gapSize,
  userData,
}: Readonly<StableLineProps>) => {
  const size = useThree((state) => state.size);
  // `<primitive object={line}>` never changes its props, so r3f has nothing to
  // invalidate when only the geometry's buffers or the material's uniforms are
  // written below. On a `frameloop="demand"` canvas that leaves an
  // appearance-only change — a guide stroke recolouring, an opacity step —
  // invisible until something unrelated schedules a frame.
  const invalidate = useThree((state) => state.invalidate);

  // Built in a committed effect, not a `useMemo`. Under StrictMode React
  // replays effects mount -> unmount -> mount, so a disposal cleanup paired
  // with a memoized object disposes the geometry and material that the
  // surviving object still has attached — releasing the program and re-linking
  // it on the next frame, which is the exact stall this component exists to
  // prevent. Creating them here means the replayed cleanup disposes only the
  // copy it created. Costs one extra commit before the stroke first appears.
  // Same rule as `ProductViewerPostProcessing`: native resources belong to a
  // committed effect.
  const [resources, setResources] = useState<{
    line: Line2 | LineSegments2;
    geometry: LineGeometry | LineSegmentsGeometry;
    material: LineMaterial;
  } | null>(null);

  // Keyed on `segments` alone: the two classes are not interchangeable, but
  // nothing else about a stroke justifies rebuilding it.
  useLayoutEffect(() => {
    const material = new LineMaterial();
    const geometry = segments ? new LineSegmentsGeometry() : new LineGeometry();
    const line = segments
      ? new LineSegments2(geometry as LineSegmentsGeometry, material)
      : new Line2(geometry as LineGeometry, material);
    setResources({ line, geometry, material });
    return () => {
      setResources(null);
      geometry.dispose();
      material.dispose();
    };
  }, [segments]);

  const line = resources?.line;
  const geometry = resources?.geometry;
  const material = resources?.material;

  useLayoutEffect(() => {
    if (!geometry || !line) return;
    // Disposed first, and that is not redundant: neither `setPositions` nor
    // `computeLineDistances` writes into the existing buffers. Each allocates
    // a fresh `InstancedInterleavedBuffer` and `setAttribute`s over the old
    // pair, and three deletes a `WebGLBuffer` only from `onGeometryDispose`,
    // which iterates the attributes the geometry holds *at that moment*. So
    // without this every change of the points would orphan up to four buffers
    // for the life of the context — hundreds per gesture. Disposing frees the
    // previous set; the geometry object stays usable and `WebGLGeometries.get`
    // re-registers it on the next render. The material is deliberately not
    // disposed, which is the whole point of this component.
    geometry.dispose();
    geometry.setPositions(flatten(points));
    // Dash spacing is measured along the line, so it has to be recomputed
    // whenever the points move, dashed or not — a stroke that becomes dashed
    // later would otherwise carry distances from a previous shape.
    line.computeLineDistances();
    invalidate();
  }, [points, geometry, line, invalidate]);

  useLayoutEffect(() => {
    if (!material) return;
    material.color.set(color);
    material.opacity = opacity;
    material.linewidth = lineWidth;
    material.transparent = true;
    material.depthTest = false;
    // `dashed` is a define, so this is the one material change that costs a
    // recompile. `LineMaterial` exposes it as an accessor that writes
    // `defines.USE_DASH` and raises `needsUpdate` only when the flag actually
    // flips, so setting it here is enough — writing the define by hand would
    // repeat the setter and an unconditional `needsUpdate` would re-key the
    // program for a stroke whose dash state never changed.
    material.dashed = dashed;
    if (dashSize !== undefined) material.dashSize = dashSize;
    if (gapSize !== undefined) material.gapSize = gapSize;
    invalidate();
  }, [
    material,
    color,
    opacity,
    lineWidth,
    dashed,
    dashSize,
    gapSize,
    invalidate,
  ]);

  useLayoutEffect(() => {
    if (!material) return;
    // Line width is in screen space, so the material has to know the buffer.
    material.resolution.set(size.width, size.height);
    invalidate();
  }, [material, size.width, size.height, invalidate]);

  useLayoutEffect(() => {
    if (!line) return;
    line.renderOrder = renderOrder;
    if (userData) Object.assign(line.userData, userData);
  }, [line, renderOrder, userData]);

  if (!line) return null;
  return <primitive object={line} />;
};
